<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { computed, type HTMLAttributes, ref } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * shadcn-vue `Switch` on the theme: a square track (BG §17.2) on the `--input` hairline that fills with the
 * command cyan when on and carries a black thumb, the same pair as the primary button and the checked
 * `Checkbox` (BG §22.4). Off, it sits on the page ground with a muted thumb. Upstream's pill radius and its
 * outline suppression are gone, so the `@layer base` `:focus-visible` ring shows (BG §22.3).
 *
 * It draws the same element, attributes and states as Reka's `SwitchRoot` / `SwitchThumb` (`role="switch"`,
 * `aria-checked`, `data-state`, `data-disabled`), on Reka's `Primitive`, but as one element: Reka's root also
 * renders a hidden form input beside the button, so a switch built on it has no single element to be. This one
 * is the button itself, so whatever a screen gives it (an `aria-label`, a test id) lands on the switch, and the
 * component is the control a screen holds. A native button already flips on Enter and Space.
 */
const props = withDefaults(
    defineProps<{
        modelValue?: boolean
        defaultValue?: boolean
        disabled?: boolean
        class?: HTMLAttributes['class']
    }>(),
    { modelValue: undefined, defaultValue: false, disabled: false, class: undefined },
)

const emits = defineEmits<{
    'update:modelValue': [value: boolean]
}>()

/**
 * Controlled whenever the parent passes a boolean, uncontrolled (on its own state, seeded from `defaultValue`)
 * only while it passes nothing. That is decided on every read, not once at setup, so a parent whose value is
 * still `undefined` while it loads takes control the moment it supplies one, and a click never shows a state
 * the parent has not accepted.
 */
const local = ref(props.defaultValue)
const controlled = computed(() => props.modelValue !== undefined)
const checked = computed(() => (controlled.value ? props.modelValue : local.value) === true)
const state = computed(() => (checked.value ? 'checked' : 'unchecked'))

function toggle(): void {
    if (props.disabled) {
        return
    }
    const next = !checked.value
    if (!controlled.value) {
        local.value = next
    }
    emits('update:modelValue', next)
}
</script>

<template>
  <Primitive
    as="button"
    type="button"
    role="switch"
    data-slot="switch"
    :aria-checked="checked"
    :data-state="state"
    :data-disabled="disabled ? '' : undefined"
    :disabled="disabled"
    :class="
      cn(
        'peer border-input data-[state=unchecked]:bg-background data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 inline-flex h-6 w-11 shrink-0 items-center border p-0.5 transition-colors focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50',
        props.class,
      )
    "
    @click="toggle"
  >
    <span
      data-slot="switch-thumb"
      :data-state="state"
      class="data-[state=unchecked]:bg-muted-foreground data-[state=checked]:bg-primary-foreground pointer-events-none block size-4.5 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
    />
  </Primitive>
</template>
